/**
 * baby: Constellation Workbench: fixed-height mat, wheel/touch scrub (no tall sticky track).
 * Progress 0→1 drives SVG twine + stamp viewfinder. Page scroll resumes at ends.
 */

import type { CraftConstellationConfig, CraftConstellationSection } from './craft-constellation-config'

type Segment = {
  kind: 'dive' | 'conn'
  si: number
  clip: string
  still: string
  accent: string
  w: number
  linger: number
  start: number
  end: number
  target: number
  cur: number
  video: HTMLVideoElement | null
  stillEl: HTMLImageElement | null
  el: HTMLElement | null
  ready: boolean
  loading: boolean
  visible: boolean
}

export type ConstellationController = {
  destroy: () => void
  jumpTo: (sectionIndex: number) => void
  getActiveIndex: () => number
}

const clamp = (x: number, a = 0, b = 1) => Math.min(b, Math.max(a, x))
const smooth = (x: number) => {
  const t = clamp(x)
  return t * t * (3 - 2 * t)
}
const lingerEase = (x: number, L: number) => {
  const l = clamp(L)
  const c = x - 0.5
  return (1 - l) * x + l * (4 * c * c * c + 0.5)
}

export const mountCraftConstellation = (
  root: HTMLElement,
  config: CraftConstellationConfig,
  onActiveChange?: (index: number) => void,
): ConstellationController => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const coarse = window.matchMedia('(hover: none) and (pointer: coarse)').matches
  const sections = config.sections
  const connectors = config.connectors
  const N = sections.length
  const diveW = config.diveScroll
  const connW = config.connScroll
  const crossfade = config.crossfade
  // baby: Wheel delta pixels needed to finish the full flight (snappy, not endless).
  const flightWheelPx = Math.max(400, (config.flightBudgetVh || 2.15) * 420)

  root.classList.add('cc-root')
  root.replaceChildren()

  // baby: Track is ONLY the mat height: never a multi-viewport scroll spacer.
  const track = document.createElement('div')
  track.className = 'cc-track'

  const stage = document.createElement('div')
  stage.className = 'cc-sticky'
  stage.setAttribute('tabindex', '0')
  stage.setAttribute('role', 'region')
  stage.setAttribute('aria-label', 'Scrollable NINX constellation workbench')

  stage.innerHTML = `
    <div class="cc-mat" aria-hidden="false">
      <svg class="cc-map" viewBox="0 0 1000 600" role="img" aria-label="NINX constellation nodes">
        <path class="cc-twine cc-twine--rail" fill="none" stroke-linecap="round" stroke-linejoin="round" />
        <path class="cc-twine cc-twine--draw" fill="none" stroke-linecap="round" stroke-linejoin="round" />
        <g class="cc-nodes"></g>
      </svg>
      <div class="cc-stamp">
        <span class="cc-stamp__tape cc-stamp__tape--tl" aria-hidden="true"></span>
        <span class="cc-stamp__tape cc-stamp__tape--tr" aria-hidden="true"></span>
        <div class="cc-stamp__stage"></div>
        <div class="cc-stamp__caption">
          <p class="cc-stamp__eyebrow"></p>
          <p class="cc-stamp__title"></p>
        </div>
      </div>
      <aside class="cc-tag">
        <p class="cc-tag__eyebrow"></p>
        <h3 class="cc-tag__title"></h3>
        <p class="cc-tag__body"></p>
        <ul class="cc-tag__tags"></ul>
      </aside>
      <p class="cc-hint"><span></span><i></i></p>
    </div>
  `

  root.appendChild(track)
  track.appendChild(stage)

  const map = stage.querySelector<SVGSVGElement>('.cc-map')!
  const twineRail = stage.querySelector<SVGPathElement>('.cc-twine--rail')!
  const twineDraw = stage.querySelector<SVGPathElement>('.cc-twine--draw')!
  const nodesG = stage.querySelector<SVGGElement>('.cc-nodes')!
  const stampStage = stage.querySelector<HTMLElement>('.cc-stamp__stage')!
  const tagEyebrow = stage.querySelector<HTMLElement>('.cc-tag__eyebrow')!
  const tagTitle = stage.querySelector<HTMLElement>('.cc-tag__title')!
  const tagBody = stage.querySelector<HTMLElement>('.cc-tag__body')!
  const tagTags = stage.querySelector<HTMLElement>('.cc-tag__tags')!
  const stampEyebrow = stage.querySelector<HTMLElement>('.cc-stamp__eyebrow')!
  const stampTitle = stage.querySelector<HTMLElement>('.cc-stamp__title')!
  const hint = stage.querySelector<HTMLElement>('.cc-hint')!
  const hintText = hint.querySelector('span')!
  hintText.textContent = config.hint

  const pathD = sections
    .map((s, i) => `${i === 0 ? 'M' : 'L'} ${s.node.x} ${s.node.y}`)
    .join(' ')
  twineRail.setAttribute('d', pathD)
  twineDraw.setAttribute('d', pathD)

  const nodeButtons: SVGCircleElement[] = []
  sections.forEach((s, i) => {
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    g.classList.add('cc-node')
    g.dataset.index = String(i)

    const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    ring.setAttribute('cx', String(s.node.x))
    ring.setAttribute('cy', String(s.node.y))
    ring.setAttribute('r', '22')
    ring.classList.add('cc-node__ring')

    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    dot.setAttribute('cx', String(s.node.x))
    dot.setAttribute('cy', String(s.node.y))
    dot.setAttribute('r', '10')
    dot.classList.add('cc-node__dot')
    dot.style.setProperty('--cc-accent', s.accent)

    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    label.setAttribute('x', String(s.node.x))
    label.setAttribute('y', String(s.node.y + 42))
    label.setAttribute('text-anchor', 'middle')
    label.classList.add('cc-node__label')
    label.textContent = s.label

    g.appendChild(ring)
    g.appendChild(dot)
    g.appendChild(label)
    nodesG.appendChild(g)
    nodeButtons.push(dot)

    g.style.cursor = 'pointer'
    g.addEventListener('click', () => jumpTo(i))
  })

  const diveBySection = new Map<number, Segment>()
  const segments: Segment[] = []
  sections.forEach((s, i) => {
    const dive: Segment = {
      kind: 'dive',
      si: i,
      clip: s.clip,
      still: s.still,
      accent: s.accent,
      w: s.scroll || diveW,
      linger: s.linger || 0,
      start: 0,
      end: 0,
      target: 0,
      cur: 0,
      video: null,
      stillEl: null,
      el: null,
      ready: false,
      loading: false,
      visible: false,
    }
    segments.push(dive)
    diveBySection.set(i, dive)

    if (i < N - 1 && connectors[i]) {
      segments.push({
        kind: 'conn',
        si: i,
        clip: connectors[i]!,
        still: sections[i + 1]!.still,
        accent: sections[i + 1]!.accent,
        w: connW,
        linger: 0,
        start: 0,
        end: 0,
        target: 0,
        cur: 0,
        video: null,
        stillEl: null,
        el: null,
        ready: false,
        loading: false,
        visible: false,
      })
    }
  })

  // baby: Normalize weights to a 0-1 progress timeline (not document scroll px).
  const weightSum = segments.reduce((sum, s) => sum + s.w, 0) || 1
  let off = 0
  segments.forEach((s) => {
    s.start = off
    off += s.w / weightSum
    s.end = off
  })

  segments.forEach((seg) => {
    const layer = document.createElement('div')
    layer.className = 'cc-layer'
    const img = document.createElement('img')
    img.className = 'cc-layer__still'
    img.alt = ''
    img.decoding = 'async'
    img.loading = 'lazy'
    img.src = seg.still
    layer.appendChild(img)
    stampStage.appendChild(layer)
    seg.stillEl = img
    seg.el = layer
  })

  let pathLen = 0
  let progress = 0
  let activeIndex = -1
  let rafId = 0
  let destroyed = false
  let inView = false
  let touchY = 0

  const measurePath = () => {
    try {
      pathLen = twineDraw.getTotalLength()
    } catch {
      pathLen = 1
    }
    twineRail.style.strokeDasharray = String(pathLen)
    twineDraw.style.strokeDasharray = String(pathLen)
    twineDraw.style.strokeDashoffset = String(pathLen)
  }

  const setCopy = (s: CraftConstellationSection) => {
    tagEyebrow.textContent = s.eyebrow
    tagTitle.textContent = s.title
    tagBody.textContent = s.body
    stampEyebrow.textContent = s.eyebrow
    stampTitle.textContent = s.title
    tagTags.replaceChildren()
    s.tags.forEach((t) => {
      const li = document.createElement('li')
      li.textContent = t
      tagTags.appendChild(li)
    })
  }

  const loadClip = (s: Segment) => {
    if (reduce || s.loading || !s.clip || s.video) return
    s.loading = true
    fetch(s.clip)
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('404'))))
      .then((blob) => {
        if (destroyed) return
        const v = document.createElement('video')
        v.className = 'cc-layer__video'
        v.muted = true
        v.playsInline = true
        v.preload = 'auto'
        v.setAttribute('muted', '')
        v.setAttribute('playsinline', '')
        v.src = URL.createObjectURL(blob)
        v.addEventListener('loadedmetadata', () => {
          s.ready = true
          paint()
        })
        v.addEventListener(
          'seeked',
          () => {
            s.el?.classList.add('has-clip')
          },
          { once: true },
        )
        s.el?.appendChild(v)
        s.video = v
      })
      .catch(() => {
        s.loading = false
      })
  }

  const paint = () => {
    const p = progress
    const fade = Math.max(0.04, crossfade)

    let ci = 0
    for (let i = 0; i < segments.length; i++) {
      if (p >= segments[i]!.start) ci = i
    }

    if (pathLen > 0) {
      twineDraw.style.strokeDashoffset = String(pathLen * (1 - p))
    }

    const pastWorkbench = p >= 0.995

    for (let i = 0; i < segments.length; i++) {
      const s = segments[i]!
      if (p > s.start - 0.15 && p < s.end + 0.15) loadClip(s)
      const local = clamp((p - s.start) / (s.end - s.start || 1), 0, 1)
      s.target = s.linger ? lingerEase(local, s.linger) : local
      let outside = 0
      if (p < s.start) outside = s.start - p
      else if (p > s.end) outside = p - s.end
      const op =
        i === segments.length - 1 && pastWorkbench
          ? 1
          : smooth(1 - outside / (fade || 1))
      if (s.el) {
        s.el.style.opacity = String(op)
        s.el.style.zIndex = i === ci ? '5' : String(1 + Math.round(op * 3))
      }
      s.visible = op > 0.001
    }

    const cur = segments[ci]!
    const near = clamp(
      cur.kind === 'dive'
        ? cur.si
        : (p - cur.start) / (cur.end - cur.start || 1) > 0.5
          ? cur.si + 1
          : cur.si,
      0,
      N - 1,
    )

    if (near !== activeIndex) {
      activeIndex = near
      nodeButtons.forEach((dot, k) => {
        const g = dot.parentElement
        g?.classList.toggle('is-active', k === near)
        g?.classList.toggle('is-done', k < near)
      })
      setCopy(sections[near]!)
      root.style.setProperty('--cc-accent', sections[near]!.accent)
      onActiveChange?.(near)
    }

    hint.style.opacity = String(pastWorkbench ? 0 : clamp(1 - p / 0.12))
    root.classList.toggle('cc-root--past', pastWorkbench)
    root.classList.toggle('cc-root--armed', inView && !pastWorkbench && p < 0.995)
  }

  const jumpTo = (i: number) => {
    const seg = diveBySection.get(i)
    if (!seg) return
    progress = clamp(seg.start + (seg.end - seg.start) * 0.45)
    paint()
  }

  /** baby: Capture wheel only while the mat is on screen and flight isn't finished. */
  const tryCaptureDelta = (deltaY: number): boolean => {
    if (reduce || !inView) return false
    const goingDown = deltaY > 0
    const goingUp = deltaY < 0
    // At ends, release page scroll so Start here / previous sections can move.
    if (goingDown && progress >= 0.999) return false
    if (goingUp && progress <= 0.001) return false

    progress = clamp(progress + deltaY / flightWheelPx)
    paint()
    return true
  }

  const onWheel = (e: WheelEvent) => {
    updateInView()
    if (tryCaptureDelta(e.deltaY)) {
      e.preventDefault()
    }
  }

  const onTouchStart = (e: TouchEvent) => {
    touchY = e.touches[0]?.clientY ?? 0
  }

  const onTouchMove = (e: TouchEvent) => {
    updateInView()
    const y = e.touches[0]?.clientY ?? touchY
    const dy = touchY - y
    touchY = y
    if (Math.abs(dy) < 0.5) return
    if (tryCaptureDelta(dy * 1.35)) {
      e.preventDefault()
    }
  }

  const updateInView = () => {
    const rect = stage.getBoundingClientRect()
    const vh = window.innerHeight || 1
    // baby: Armed when a good chunk of the mat is visible (not only dead-center).
    const visible = Math.min(rect.bottom, vh) - Math.max(rect.top, 0)
    inView = visible > Math.min(rect.height, vh) * 0.35
    root.classList.toggle('cc-root--armed', inView && progress < 0.995)
  }

  const onScrollOrResize = () => {
    updateInView()
  }

  const raf = () => {
    if (destroyed) return
    const eps = coarse ? 0.02 : 0.008
    for (let i = 0; i < segments.length; i++) {
      const s = segments[i]!
      if (!s.video || !s.ready) continue
      if (s.video.seeking) continue
      if (!s.visible && Math.abs(s.cur - s.target) < 0.002) continue
      s.cur += (s.target - s.cur) * (reduce ? 1 : 0.22)
      const dur = s.video.duration || 1
      const t = clamp(s.cur, 0, 0.999) * dur
      if (Math.abs(s.video.currentTime - t) > eps) {
        try {
          s.video.currentTime = t
        } catch {
          /* ignore seek races */
        }
      }
    }
    rafId = requestAnimationFrame(raf)
  }

  setCopy(sections[0]!)
  measurePath()
  paint()
  updateInView()

  // baby: Window wheel while armed: scrub without a tall document track.
  window.addEventListener('wheel', onWheel, { passive: false })
  window.addEventListener('touchstart', onTouchStart, { passive: true })
  window.addEventListener('touchmove', onTouchMove, { passive: false })
  window.addEventListener('scroll', onScrollOrResize, { passive: true })
  window.addEventListener('resize', onScrollOrResize)
  rafId = requestAnimationFrame(raf)

  void map

  return {
    destroy: () => {
      destroyed = true
      cancelAnimationFrame(rafId)
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('scroll', onScrollOrResize)
      window.removeEventListener('resize', onScrollOrResize)
      segments.forEach((s) => {
        if (s.video?.src.startsWith('blob:')) URL.revokeObjectURL(s.video.src)
      })
      root.replaceChildren()
      root.classList.remove('cc-root', 'cc-root--past', 'cc-root--armed')
    },
    jumpTo,
    getActiveIndex: () => activeIndex,
  }
}
